import { useState } from 'react';
import { motion } from 'framer-motion';
import { Save, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { BackButton } from '@/components/ui/BackButton';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { format, parseISO } from 'date-fns';
import { useCustomTags } from '@/hooks/useCustomTags';
import { moodOptions } from '@/data/journalPrompts';
import { JournalEntry } from '@/hooks/useJournal';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const presetTags = [
  { value: 'gratitude', label: 'Gratitude' },
  { value: 'reflection', label: 'Reflection' },
  { value: 'dua', label: 'Dua' },
  { value: 'intention', label: 'Intention' },
  { value: 'lesson', label: 'Lesson' },
  { value: 'outpouring', label: 'Outpouring' },
];

interface NewReflectionViewProps {
  onBack: () => void;
  onSave: (entry: {
    title: string;
    reflection: string;
    tags: string[];
    mood?: string;
    gratitude?: string;
    needsFromAllah?: string;
  }) => Promise<void>;
  initialFeelings?: string[];
  editEntry?: JournalEntry;
  onDelete?: () => void;
}

export const NewReflectionView = ({ onBack, onSave, initialFeelings = [], editEntry, onDelete }: NewReflectionViewProps) => {
  const isEditMode = !!editEntry;

  const [title, setTitle] = useState(editEntry?.title || '');
  const [reflection, setReflection] = useState(editEntry?.reflection || '');
  const [selectedTags, setSelectedTags] = useState<string[]>(editEntry?.tags || []);
  const [mood, setMood] = useState(editEntry?.mood || '');
  const [gratitude, setGratitude] = useState(editEntry?.gratitude || '');
  const [needsFromAllah, setNeedsFromAllah] = useState(editEntry?.needsFromAllah || '');
  const [isSaving, setIsSaving] = useState(false);
  const [showCustomDialog, setShowCustomDialog] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [tagError, setTagError] = useState('');
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  
  const { customTags, addCustomTag, maxReached } = useCustomTags();

  const feelingLabels = initialFeelings.map(f => 
    f.charAt(0).toUpperCase() + f.slice(1)
  );

  const allTags = [
    ...presetTags,
    ...customTags.map(t => ({ value: t.toLowerCase(), label: t })),
  ];

  const toggleTag = (tag: string) => {
    setSelectedTags(prev =>
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );
  };

  const hasChanges = isEditMode ? (
    title !== (editEntry?.title || '') ||
    reflection !== (editEntry?.reflection || '') ||
    mood !== (editEntry?.mood || '') ||
    gratitude !== (editEntry?.gratitude || '') ||
    needsFromAllah !== (editEntry?.needsFromAllah || '') ||
    JSON.stringify(selectedTags) !== JSON.stringify(editEntry?.tags || [])
  ) : (title.trim() || reflection.trim());

  const handleBack = () => {
    if (hasChanges) {
      setShowDiscardDialog(true);
    } else {
      onBack();
    }
  };

  const handleSave = async () => {
    if (!reflection.trim() && !gratitude.trim() && !needsFromAllah.trim()) return;
    setIsSaving(true);
    await onSave({
      title: title.trim(),
      reflection: reflection.trim(),
      tags: selectedTags,
      mood,
      gratitude: gratitude.trim(),
      needsFromAllah: needsFromAllah.trim(),
    });
    setIsSaving(false);
  };

  const handleAddCustomTag = () => {
    setTagError('');
    const result = addCustomTag(newTagName);
    if (result.success) {
      setNewTagName('');
      setShowCustomDialog(false);
    } else {
      setTagError(result.error || 'Invalid tag');
    }
  };

  const canSave = reflection.trim().length > 0 || gratitude.trim().length > 0 || needsFromAllah.trim().length > 0;

  const dateDisplay = isEditMode && editEntry?.date
    ? format(parseISO(editEntry.date), 'EEEE, MMMM d, yyyy')
    : format(new Date(), 'EEEE, MMMM d');

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen bg-paper"
    >
      {/* Header */}
      <div className="bg-card border-b border-border sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <BackButton onClick={handleBack} srLabel={isEditMode ? 'Return to journal' : 'Return to check-in'} />
            <div className="text-center">
              <div className="flex items-center justify-center gap-2">
                <h1 className="font-display text-lg text-foreground">
                  {isEditMode ? 'Edit Reflection' : 'New Reflection'}
                </h1>
                {isEditMode && (
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-wider bg-primary/10 text-primary">
                    Editing
                  </span>
                )}
              </div>
              <p className="text-xs text-muted-foreground">{dateDisplay}</p>
            </div>
            <div className="w-10" />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="space-y-8">
          {/* Feeling Tags from Check-In (new entries only) */}
          {!isEditMode && feelingLabels.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {feelingLabels.map((feeling) => (
                <span
                  key={feeling}
                  className="px-3 py-1 rounded-full text-xs bg-primary/10 text-primary"
                >
                  {feeling}
                </span>
              ))}
            </div>
          )}

          {/* Mood Selection */}
          <div className="space-y-3">
            <p className="text-sm text-foreground/90 font-medium">How is your heart today?</p>
            <div className="flex flex-wrap gap-2">
              {moodOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => setMood(mood === option.value ? '' : option.value)}
                  className={`emotion-pill ${mood === option.value ? 'emotion-pill-selected' : ''} flex items-center gap-2 px-3 py-2 rounded-full text-sm transition-all duration-300 ${
                    mood === option.value
                      ? 'text-foreground'
                      : 'bg-background/80 text-foreground/80'
                  }`}
                >
                  <span>{option.emoji}</span>
                  <span>{option.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Title Field */}
          <div className="space-y-2">
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Give this reflection a title…"
              className="border-0 border-b border-dashed border-border rounded-none px-0 text-lg font-display bg-transparent focus-visible:ring-0 focus-visible:border-primary/50 placeholder:text-muted-foreground/60"
            />
          </div>

          {/* Gratitude */}
          <div className="space-y-2">
            <label className="text-sm text-foreground/90 font-medium">What are you grateful for?</label>
            <Textarea
              value={gratitude}
              onChange={(e) => setGratitude(e.target.value)}
              placeholder="Write your gratitude here…"
              className="min-h-[100px] border-dashed bg-cream/30 focus:bg-cream/50 transition-colors resize-none text-foreground/90 leading-relaxed placeholder:text-muted-foreground/60"
            />
          </div>

          {/* Main Reflection Field */}
          <div className="space-y-2">
            <label className="text-sm text-foreground/90 font-medium">Daily Reflection</label>
            <Textarea
              value={reflection}
              onChange={(e) => setReflection(e.target.value)}
              placeholder="Write your reflection, thoughts, or lessons here…"
              className="min-h-[200px] border-dashed bg-cream/30 focus:bg-cream/50 transition-colors resize-none text-foreground/90 leading-relaxed placeholder:text-muted-foreground/60"
            />
          </div>

          {/* Needs from Allah */}
          <div className="space-y-2">
            <label className="text-sm text-foreground/90 font-medium">What do you need from Allah?</label>
            <Textarea
              value={needsFromAllah}
              onChange={(e) => setNeedsFromAllah(e.target.value)}
              placeholder="Pour out your heart to Allah…"
              className="min-h-[100px] border-dashed bg-cream/30 focus:bg-cream/50 transition-colors resize-none text-foreground/90 leading-relaxed placeholder:text-muted-foreground/60"
            />
          </div>

          {/* Tags */}
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">
              Optional Tags
            </p>
            <div className="flex flex-wrap gap-2">
              {allTags.map((tag) => (
                <button
                  key={tag.value}
                  onClick={() => toggleTag(tag.value)}
                  className={`emotion-pill ${selectedTags.includes(tag.value) ? 'emotion-pill-selected' : ''} px-[1.5rem] py-[0.65rem] rounded-full text-sm transition-all duration-300 ${
                    selectedTags.includes(tag.value)
                      ? 'text-foreground'
                      : 'bg-background/80 text-foreground/70'
                  }`}
                >
                  {tag.label}
                </button>
              ))}
              
              {/* + Add Custom button */}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => !maxReached && setShowCustomDialog(true)}
                      disabled={maxReached}
                      className={`emotion-pill px-[1.5rem] py-[0.65rem] rounded-full text-sm transition-all duration-300 inline-flex items-center gap-1.5 ${
                        maxReached
                          ? 'opacity-50 cursor-not-allowed text-muted-foreground'
                          : 'bg-background/80 text-foreground/70 hover:text-foreground'
                      }`}
                    >
                      <Plus className="h-3.5 w-3.5" />
                      Add Custom
                    </button>
                  </TooltipTrigger>
                  {maxReached && (
                    <TooltipContent>
                      <p>Maximum 5 custom tags reached</p>
                    </TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>

          {/* Actions */}
          <div className="pt-4 space-y-3">
            <Button
              onClick={handleSave}
              disabled={!canSave || isSaving || (isEditMode && !hasChanges)}
              className="w-full h-12 text-base gap-2"
            >
              {isSaving ? 'Saving…' : (
                <>
                  <Save className="h-4 w-4" />
                  {isEditMode ? 'Save Changes' : 'Save Reflection'}
                </>
              )}
            </Button>

            {isEditMode && onDelete && (
              <Button
                variant="ghost"
                onClick={() => setShowDeleteDialog(true)}
                className="w-full text-destructive hover:text-destructive hover:bg-destructive/10 gap-2"
              >
                <Trash2 className="h-4 w-4" />
                Delete Entry
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Custom Tag Dialog */}
      <Dialog open={showCustomDialog} onOpenChange={setShowCustomDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-display">Create Custom Tag (5 max)</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Input
                value={newTagName}
                onChange={(e) => {
                  setNewTagName(e.target.value.slice(0, 15));
                  setTagError('');
                }}
                placeholder="Tag name (e.g., Quran, Family, Prayer)"
                maxLength={15}
                onKeyDown={(e) => e.key === 'Enter' && handleAddCustomTag()}
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>{tagError && <span className="text-destructive">{tagError}</span>}</span>
                <span>{newTagName.length}/15</span>
              </div>
            </div>
            {customTags.length > 0 && (
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">Your custom tags:</p>
                <div className="flex flex-wrap gap-1.5">
                  {customTags.map(t => (
                    <span key={t} className="px-2 py-0.5 rounded-full text-xs bg-cream text-foreground/70">{t}</span>
                  ))}
                </div>
              </div>
            )}
            {customTags.length === 0 && (
              <p className="text-xs text-muted-foreground">Create tags for themes like Prayer, Quran, Family</p>
            )}
            <Button onClick={handleAddCustomTag} disabled={!newTagName.trim()} className="w-full">
              Create Tag
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Discard Changes Dialog */}
      <AlertDialog open={showDiscardDialog} onOpenChange={setShowDiscardDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Are you sure you want to go back?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Editing</AlertDialogCancel>
            <AlertDialogAction onClick={onBack} className="bg-destructive hover:bg-destructive/90">
              Discard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation Dialog */}
      {isEditMode && onDelete && (
        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Journal Entry?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete your entry from {dateDisplay}. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={onDelete} className="bg-destructive hover:bg-destructive/90">
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </motion.div>
  );
};
